'use client';

import { useRef } from 'react';

/**
 * Highest progress fraction a re-anchored list may start from. The per-render
 * stage list arrives mid-flight (the prompt LLM has to finish first), so the
 * list is replayed over whatever progress is LEFT. Past this point there is not
 * enough room left to narrate anything, so the list is simply compressed into
 * the tail. Shared constant across WEB / Android / Apple.
 */
const MAX_ANCHOR = 0.85;

interface Pacing {
  /** Identity of the list these thresholds were built for. */
  signature: string;
  /** Cumulative, normalized upper-bound progress fraction per stage. */
  thresholds: number[];
}

/**
 * Picks the active "design stage" to narrate above the render progress bar
 * from the current render progress (0..100).
 *
 * The stages advance with progress (honest — never runs ahead of the render),
 * but each stage gets a RANDOMIZED dwell weight so some linger longer than
 * others: the line feels like live, uneven work rather than an even pendulum.
 * Weights are seeded once per list, so every render paces differently.
 *
 * The list is replaced mid-render: the frontend starts on the static fallback
 * and swaps to the per-render list once the prompt LLM has tailored it. When
 * that happens we re-anchor to the CURRENT progress and spread the new list
 * over the remaining span — otherwise the pacer would jump straight into the
 * middle of the fresh list and its opening stages would never be shown.
 *
 * Mirrors Apple `DesignStagePacer` and Android `rememberDesignStage`.
 */
export function useDesignStage(
  stages: string[] | undefined,
  progress: number,
  isActive: boolean
): string | null {
  // A ref (not state) keeps the pacing stable across the many progress
  // re-renders without re-seeding, and lets us capture the anchor at the exact
  // moment the list changes.
  const pacingRef = useRef<Pacing | null>(null);

  const fraction = Math.min(Math.max(progress / 100, 0), 1);
  // Keyed on the CONTENT, not the array identity: a fresh fetch returns a new
  // array reference every time but the same stage list — reseeding on that
  // would make the pacing jump mid-render.
  const signature = stages?.join(' ') ?? '';

  if (!pacingRef.current || pacingRef.current.signature !== signature) {
    // The first list of this mount starts at 0 (a screen reopened mid-render
    // must show the stage the render is honestly at). A REPLACEMENT list
    // re-anchors to the current progress and plays out over what is left.
    const anchor = pacingRef.current ? Math.min(fraction, MAX_ANCHOR) : 0;
    pacingRef.current = {
      signature,
      thresholds: buildThresholds(stages?.length ?? 0, anchor),
    };
  }

  if (!isActive || !stages || stages.length === 0) return null;

  const { thresholds } = pacingRef.current;
  let index = thresholds.findIndex((t) => t > fraction);
  if (index < 0) index = stages.length - 1;
  return stages[Math.min(index, stages.length - 1)] ?? null;
}

function buildThresholds(count: number, anchor: number): number[] {
  if (count === 0) return [];

  // Weight in [0.85, 1.15): a small jitter around an even dwell so the pacing
  // feels alive but never lets one stage linger while another flashes.
  const weights = Array.from(
    { length: count },
    () => 0.85 + Math.random() * 0.3
  );
  const cumulative: number[] = [];
  let sum = 0;
  for (const w of weights) {
    sum += w;
    cumulative.push(sum);
  }
  if (sum <= 0) return [];

  // Normalize to (0, 1], then map onto the span left after the anchor — each
  // entry is the upper progress-fraction bound of its stage's band.
  return cumulative.map((c) => anchor + (1 - anchor) * (c / sum));
}
